import React from 'react';
import classNames from 'classnames';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import { buttonVariants } from 'components/partials/button/button';

import Icons from 'assets/icons';

interface LessonClickArgs {
  event?: React.MouseEvent<HTMLAnchorElement>;
  to: string;
  id: number;
}

interface LessonNavigationProps {
  lessons: string[];
  currentLessonId: string | number;
  courseId: string | number;
  handleLessonClick: (args: LessonClickArgs) => void;
}

interface LessonTarget {
  to: string;
  id: number;
}

const LessonNavigation = ({
  lessons,
  currentLessonId,
  courseId,
  handleLessonClick,
}: LessonNavigationProps): React.JSX.Element => {
  const currentIndex = lessons.findIndex(
    (id) => id == `lesson-${currentLessonId}`
  );

  // currentIndex is -1 while lessons haven't loaded yet or the current
  // lesson id doesn't (yet) match an entry — treat that as "no adjacent
  // lesson" rather than computing a target from an out-of-range index
  // (e.g. lessons[-2]), which previously threw on render.
  const hasCurrentLesson = currentIndex !== -1;
  const isFirstLesson = !hasCurrentLesson || currentIndex === 0;
  const isLastLesson = !hasCurrentLesson || currentIndex === lessons.length - 1;

  const getLessonTarget = (newIndex: number): LessonTarget => {
    const newLessonId = lessons[newIndex];
    const lessonNumber = Number.parseInt(newLessonId.match(/\d+/)![0], 10);

    return {
      to: `/learn/courses/${courseId}/lesson/${lessonNumber}`,
      id: lessonNumber,
    };
  };

  // Navigating to another lesson is a real route change (not an in-page
  // action), so this must be a real <a href> for correct link semantics
  // (WCAG 4.1.2) — handleLessonClick still intercepts the click to save
  // scroll progress before the route swap, same pattern as the lesson
  // sidebar links.
  const previousTarget = !isFirstLesson
    ? getLessonTarget(currentIndex - 1)
    : null;
  const nextTarget = !isLastLesson ? getLessonTarget(currentIndex + 1) : null;

  const linkClassName = classNames(
    buttonVariants({ variant: 'reserve-outlined' }),
    'no-underline'
  );

  return (
    <Stack
      direction="row"
      sx={{ justifyContent: 'space-between', alignItems: 'center' }}
    >
      {previousTarget ? (
        <a
          href={previousTarget.to}
          onClick={(event) => handleLessonClick({ event, ...previousTarget })}
          className={linkClassName}
        >
          <Icons.DoubleArrow className="rotate-180" aria-hidden="true" />
          Previous Lesson
        </a>
      ) : (
        <Box sx={{ flex: '1 1 0%' }}></Box>
      )}
      {nextTarget && (
        <a
          href={nextTarget.to}
          onClick={(event) => handleLessonClick({ event, ...nextTarget })}
          className={linkClassName}
        >
          Next Lesson
          <Icons.DoubleArrow aria-hidden="true" />
        </a>
      )}
    </Stack>
  );
};

export default LessonNavigation;
